import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from '@crm/contracts';

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  request_id?: string;
  errors?: { field: string; message: string }[];
  /** 422 « téléphone déjà porté » : le compte qui le porte, s'il est dans le périmètre de la personne. */
  existing_account?: { id: string; code: string; name: string } | null;
  /** 409 au pointage : la visite déjà en cours de la personne. */
  in_progress_visit?: { id: string; account: { id: string; code: string; name: string } } | null;
  /** 409 / 422 à la confirmation d'une photo : octets reçus par le serveur (reprise). */
  uploaded_bytes?: number;
  /** 409 à la création d'un plan d'objectifs déjà existant (OpenAPI 2.2.1) : son identifiant. */
  existing_plan_id?: string;
}

/** Erreur d'API portant le Problem RFC 9457 : `detail` est la raison à afficher en premier. */
export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail);
  }
}

export interface ApiSession {
  token: () => string;
  tenantId: () => string | null;
}

export function createApi(session: ApiSession) {
  const client = createClient<paths>({ baseUrl: '/api/v1' });
  const auth: Middleware = {
    onRequest({ request }) {
      const token = session.token();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      const tenant = session.tenantId();
      if (tenant && !request.headers.has('X-Tenant-Id')) request.headers.set('X-Tenant-Id', tenant);
      return request;
    },
  };
  client.use(auth);
  return client;
}

export type Api = ReturnType<typeof createApi>;

/** Déballe une réponse openapi-fetch : renvoie les données ou lève ApiError avec la raison du serveur. */
export async function unwrapWithResponse<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<{ data: T; response: Response }> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call;
  } catch {
    throw new ApiError({ type: 'reseau', title: 'Réseau', status: 0, detail: 'Le serveur ne répond pas. Vérifiez votre connexion et réessayez.' });
  }
  if (result.error !== undefined || !result.response.ok) {
    const e = result.error as Partial<Problem> | undefined;
    throw new ApiError({
      type: e?.type ?? 'inconnu',
      title: e?.title ?? 'Erreur',
      status: e?.status ?? result.response.status,
      detail: e?.detail ?? `Le serveur a répondu ${result.response.status}.`,
      request_id: e?.request_id,
      errors: e?.errors,
      existing_account: e?.existing_account,
      in_progress_visit: e?.in_progress_visit,
      uploaded_bytes: e?.uploaded_bytes,
      existing_plan_id: e?.existing_plan_id,
    });
  }
  return { data: result.data as T, response: result.response };
}

/** Variante usuelle qui ignore les en-têtes de succès. */
export async function unwrap<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  return (await unwrapWithResponse(call)).data;
}
